import { memo } from 'react'

import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button, styled } from '@mui/material'
import { SmepEvent, SmepEventConfig } from '@nativewaves/nw-cloud-sdk/smep'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'motion/react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { appearance } from '@shared/utils/support'
import { eventConfigRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { workspaceQueryKeys } from 'services/queryKeys'

import { Panel as GlobalAudioPanel } from './GlobalAudio/Panel'
import { Panel as TopLevelPanel } from './TopLevel/Panel'
import { useConfigForm } from './useConfigForm'
import { Panel as ViewsPanel } from './Views/Panel'

const StyledForm = styled(motion.form)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  height: '100%',
  overflowY: 'auto',
}))

export type ConfigFormProps = {
  event: SmepEvent
  config: SmepEventConfig
}

const ConfigFormRaw = ({ event, config }: ConfigFormProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const { workspaceId } = eventConfigRoute.useParams()

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const repositoryId = workspaceQuery.data?.contentRepositoryId

  const { formMethods, handleSubmit, isSaving, externalChange, reloadFromServer } = useConfigForm({ event, config })

  return (
    <FormProvider {...formMethods}>
      <StyledForm onSubmit={handleSubmit} variants={appearance} initial="hidden" animate="visible">
        {externalChange && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={reloadFromServer}>
                {t('domain:Workspace.Event.ConfigEditor.reload')}
              </Button>
            }
          >
            {t('domain:Workspace.Event.ConfigEditor.externalChange')}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            type="button"
            variant="outlined"
            disabled={!formMethods.formState.isDirty || isSaving}
            onClick={() => formMethods.reset()}
          >
            {t('reset')}
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSaving} disabled={!formMethods.formState.isDirty}>
            {t('save')}
          </LoadingButton>
        </Box>
        <TopLevelPanel />
        <ViewsPanel repositoryId={repositoryId} />
        <GlobalAudioPanel repositoryId={repositoryId} />
      </StyledForm>
    </FormProvider>
  )
}

export const ConfigForm = memo(ConfigFormRaw)
